import type { FormEvent, ReactNode } from "react";

import { Modal } from "./Modal";

/**
 * A create/edit form in a modal, with Cancel and Save in the footer and the
 * server's refusal shown under the fields. `note` sits at the footer's left
 * for the one thing worth knowing before saving.
 */
export function FormModal({
  id,
  title,
  busy,
  error,
  saveLabel,
  note,
  onClose,
  onSubmit,
  children,
  size = "2xl",
}: {
  id: string;
  title: string;
  busy: boolean;
  error?: string;
  saveLabel: string;
  note?: string;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  /** Wider for a form laid out in rows, e.g. several streams at once. */
  size?: "xl" | "2xl" | "3xl" | "4xl";
}) {
  return (
    <Modal
      isOpen
      size={size}
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          {note && <p className="mr-auto text-xs text-gray-500">{note}</p>}
          <button type="button" className="btn btn-outline" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={id} className="btn btn-primary" disabled={busy}>
            {busy ? "Saving…" : saveLabel}
          </button>
        </>
      }
    >
      <form id={id} onSubmit={onSubmit} className="space-y-5">
        {children}
        {error && (
          <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
